import { user } from 'ampersand-app'
import lang from '@/lang'
import { getRouteAccessRights, checkAccessRights, checkModuleAccess, checkIsAdminRole } from '@/modules'
import isElectron from '@/modules/_helpers/isElectron'
import { isMeinBuero } from '@/utils/build-env'
import { SUBSCRIPTION_MODULES, USER_RIGHTS, SETTINGS_RIGHTS, experimentalFeatures, SubscriptionTypeTypes, MenuItems } from '@/modules/base/util/constants'
import store from '@/redux/store'
import { isOnboardingNavigationEnabled } from '@/modules/_helpers/firstSteps'
import { isUndefined, isEmpty } from 'lodash'

const isMB = isMeinBuero()
const { menu } = lang

// Menu-local USER_RIGHTS groups for rights that are not plain CRUD groups.
const REPORTING_RIGHTS = { READ: USER_RIGHTS.ACCOUNTING.REPORTING, WRITE: USER_RIGHTS.ACCOUNTING.REPORTING }

/**
 * Resolves { read, write } access for a menu item's accessRights.
 * accessRights is a list of USER_RIGHTS/SETTINGS_RIGHTS groups ({ READ, WRITE }).
 * By default all groups must grant access; pass matchAny to require only one of them
 * (for items that bundle several independent settings areas).
 *
 * @param {Array.<{READ: string, WRITE: string}>} accessRights
 * @param {object} userInfo
 * @param {boolean} [matchAny]
 * @returns {{ read: boolean, write: boolean }}
 */
const resolveMenuAccess = (accessRights, userInfo, matchAny = false) => {
  if (isEmpty(accessRights)) {
    return { read: true, write: true }
  }

  const groups = Array.isArray(accessRights) ? accessRights : [accessRights]
  if (matchAny) {
    return {
      read: groups.some(group => checkAccessRights(group.READ, userInfo)),
      write: groups.some(group => checkAccessRights(group.WRITE, userInfo)),
    }
  }
  return {
    read: checkAccessRights(groups.map(group => group.READ), userInfo),
    write: checkAccessRights(groups.map(group => group.WRITE), userInfo),
  }
}

const filterSubmenuItemsBySubscriptionModuleAccess = (items, isOwner, canSeeDisabledItems) => {
  return items
    .filter(item => {
      if (item.purchasable && isOwner) { return true }
      if (item.forceKeep) { return true }
      return checkModuleAccess(item.subscriptionModule)
    })
    .map(item => ({
      ...item,
      submenuItems: canSeeDisabledItems
        ? item.submenuItems?.filter(submenuItem => {
          return isUndefined(submenuItem.purchasable) || submenuItem.purchasable // in case purchasable is explicitly set to false hide the item instead of showing it as purchasable
        }).map((submenuItem) => {
          return {
            ...submenuItem,
            purchasable: !checkModuleAccess(submenuItem.subscriptionModule),
          }
        })
        : item.submenuItems?.filter(subItem => {
          return (subItem.purchasable && isOwner) || subItem.forceKeep || checkModuleAccess(subItem.subscriptionModule)
        }),
    }))
    .filter(item => {
      const { submenuItems, subscriptionModule, purchasable } = item
      return (!submenuItems || submenuItems.length) || subscriptionModule?.length || (purchasable && isOwner)
    })
}

const filterMenuItemsByUserAccessRights = (items, userInfo, canSeeDisabledItems) => {
  return items
    .filter(item => {
      const access = resolveMenuAccess(item.accessRights, userInfo, item.matchAnyAccessRight)
      return (item.purchasable && userInfo.isOwner) || access.write || (access.read && !item.writeAccess)
    })
    .map(item => ({
      ...item,
      submenuItems: canSeeDisabledItems
        ? item.submenuItems
        : item.submenuItems?.filter(subItem => {
          const access = resolveMenuAccess(subItem.accessRights, userInfo, subItem.matchAnyAccessRight)
          return subItem.forceKeep || access.write || access.read
        }),
    }))
    .filter(item => {
      return (!item.submenuItems || item.submenuItems.length) || item.accessRights?.length || (item.purchasable && userInfo.isOwner)
    })
}

const filterMenuItemsBySubscriptionTypeTypes = (items, subscriptions, isOwner, canSeeDisabledItems) => {
  return items
    .filter(item => {
      if (!item.subscriptionTypeTypes) { return true }
      // Product nav entries (e.g. Staff, Documents): show only when tenant has that module (existing modules), not by subscription type alone
      if (item.showOnlyWithModuleAccess) {
        return checkModuleAccess(item.subscriptionModule)
      }
      if (item.purchasable && isOwner && !item.purchasableOnlyWhenExpired) { return true }
      const hasModuleAccess = checkModuleAccess(item.subscriptionModule)
      if (hasModuleAccess) { return true }

      const subscription = subscriptions.find(sub => item.subscriptionTypeTypes.includes(sub.typeOfSubscription))
      if (item.purchasableOnlyWhenExpired && subscription && subscription.isTrial && subscription.isExpired) return false

      return Boolean(subscription)
    })
    .map(menuItem => {
      // check if the user has access to the main menu point
      const item = {
        ...menuItem,
        requiredSub: null,
        submenuItems: canSeeDisabledItems
          ? menuItem.submenuItems
          : menuItem.submenuItems?.filter(subItem => {
            if (!subItem.subscriptionTypeTypes) return true
            return subscriptions.some(sub => subItem.subscriptionTypeTypes.includes(sub.typeOfSubscription))
          }),
      }

      if (item.subscriptionTypeTypes?.length) {
        const subscription = subscriptions.find(sub => item.subscriptionTypeTypes.includes(sub.typeOfSubscription))
        // there could be users that don't have the subscription but module access
        // e.g. documents (old main sub had the document module included)
        const hasModuleAccess = item.subscriptionModule && checkModuleAccess(item.subscriptionModule)

        item.requiredSub = {
          exists: subscription || hasModuleAccess,
          isExpired: hasModuleAccess ? false : subscription?.isExpired,
          type: item.subscriptionTypeTypes[0], // always take first as there currently is no use case for multiple required subscription types
        }
      }

      return item
    })
    .filter(item => {
      return (!item.submenuItems || item.submenuItems.length) || item.subscriptionTypeTypes?.length || !item.subscriptionTypeTypes
    })
}

const removeTaxConsultantModules = (items) => {
  return items.map(item => {
    const submenuItems = item.submenuItems?.filter(subItem => subItem.name !== MenuItems.Clients)

    return { ...item, submenuItems }
  })
}

const handleSubItemsAccessRights = (items, userInfo) => {
  return items.map(item => {
    item.url = item.url || null
    let subAvailability = false
    const subItems = (item.submenuItems || []).map(subItem => {
      subItem.mainSubmenuItem = item.name
      subItem.isIncludedInPlan = checkModuleAccess(subItem.subscriptionModule) || subItem.forceKeep
      subAvailability = subAvailability || subItem.isIncludedInPlan || subItem.forceKeep
      const subRight = subItem.forceKeep
        ? { read: true, write: true }
        : getRouteAccessRights(subItem.url, userInfo)
      // link the first not disabled module to the parent
      if (subItem?.writeAccess && subRight.write === false) {
        subRight.read = false
      }
      if (subRight.read === true && !item?.url) {
        item.url = subItem.url
      }

      return { ...subItem, ...subRight }
    })

    const submenuItemsAllowed = subItems.some(subItem => subItem.isIncludedInPlan || subItem.forceKeep)

    item.isIncludedInPlan = subItems.lenght > 0 ? submenuItemsAllowed && subAvailability : true

    return { ...item, submenuItems: subItems }
  })
}

const handleAccessRights = (items) => {
  const {
    tenant: { info: tenantInfo },
    user: { info: userInfo },
    subscription: { details: { subscriptions } },
  } = store.getState()
  const showUnavailableFeatures = userInfo?.layoutSettings?.showUnavailableFeatures ?? false
  const isTaxConsultant = tenantInfo?.subscriptions?.some(s => s.isTaxConsultant)
  const hasOnlyDocSub = !tenantInfo?.subscriptions?.filter(s => s.typeOfSubscription !== SubscriptionTypeTypes.Document).length

  const canSeeDisabledItems = !hasOnlyDocSub && !isMB && !isTaxConsultant && showUnavailableFeatures && checkIsAdminRole(userInfo)

  if (tenantInfo?.subscriptions?.filter(s => s.isTaxConsultant === true)?.length) {
    const cleanedItems = filterSubmenuItemsBySubscriptionModuleAccess(items)
    return handleSubItemsAccessRights(cleanedItems, userInfo)
  } else {
    let cleanedItems = removeTaxConsultantModules(items)
    if (subscriptions.some(s => s.type === 'sharehub_free') && cleanedItems.some(item => item.name === MenuItems.Settings)) {
      const settingsIndex = cleanedItems.findIndex(item => item.name === MenuItems.Settings)
      cleanedItems[settingsIndex].submenuItems[cleanedItems[settingsIndex].submenuItems.findIndex(item => item.name === MenuItems.CustomerCenter)].forceKeep = true
    }
    if (userInfo?.options?.hideForbiddenMenuItems) {
      cleanedItems = filterMenuItemsByUserAccessRights(cleanedItems, userInfo, canSeeDisabledItems)
      cleanedItems = filterSubmenuItemsBySubscriptionModuleAccess(cleanedItems, userInfo.isOwner, canSeeDisabledItems)
      cleanedItems = filterMenuItemsBySubscriptionTypeTypes(cleanedItems, subscriptions, userInfo.isOwner, canSeeDisabledItems)
    }
    return handleSubItemsAccessRights(cleanedItems, userInfo)
  }
}

const getResolvedBackupDashboardMode = ({ backupDashboardMode, hasBackupModuleAccess, hasBackupAddon }) => {
  if (backupDashboardMode) {
    return backupDashboardMode
  }
  if (hasBackupModuleAccess) {
    return 'active'
  }
  if (hasBackupAddon === true) {
    return 'readonly'
  }
  return 'hidden'
}

export const getAppsMenuItem = (appsMenuItems, checkAccess = false) => {
  const appsItem = {
    name: MenuItems.Apps,
    title: menu.apps.title,
    submenuItems: [
      {
        name: MenuItems.Extensions,
        title: menu.apps.submenu.extensions,
        url: '/apps',
        accessRights: [
          USER_RIGHTS.APPS,
        ],
        subscriptionModule: SUBSCRIPTION_MODULES.MARKETPLACE,
      },
      ...appsMenuItems.map(item => {
        if (!item.callbackUrl) return null

        const callbackUrlParts = item.callbackUrl?.split('/')

        let name = ''
        if (callbackUrlParts.length > 0) {
          if (callbackUrlParts[1] === 'apps') {
            name = callbackUrlParts[2]
          } else {
            name = callbackUrlParts[1]
          }
        }

        return {
          // absolute callback urls yield no segment, and the name has to stay unique: it is the
          // react key of the menu entry and its data-qs-id
          name: name || item.appId,
          title: item.name,
          url: item.callbackUrl,
          internal: item.isInternalApp,
          accessRights: [
            USER_RIGHTS.APPS,
          ],
          subscriptionModule: SUBSCRIPTION_MODULES.MARKETPLACE,
        }
      }).filter(item => item !== null),
    ],
  }
  if (!checkAccess) { return appsItem }

  return handleAccessRights([appsItem])[0]
}

const mainBookingSubItems = [
  {
    name: MenuItems.DocumentExport,
    title: menu.booking.documentExport,
    url: '/document-export',
    accessRights: [
      USER_RIGHTS.ACCOUNTANT_EXPORT,
    ],
    subscriptionModule: [
      SUBSCRIPTION_MODULES.FINANCEFORM_EXPORT,
      SUBSCRIPTION_MODULES.FINANCEFORM_EXPORT_DATEV,
      SUBSCRIPTION_MODULES.FINANCEFORM_EXPORT_LIST,
      SUBSCRIPTION_MODULES.FINANCEFORM_EXPORT_DATEV_VIEW,
      SUBSCRIPTION_MODULES.FINANCEFORM_EXPORT_DOCUMENTS,
    ],
  },
  {
    name: MenuItems.VatReturn,
    title: menu.booking.vatReturn,
    url: '/taxForms/ustva',
    accessRights: [
      USER_RIGHTS.VAT_RETURN,
    ],
    subscriptionModule: SUBSCRIPTION_MODULES.VATRETURN,
  },
  {
    name: MenuItems.TaxForms,
    title: lang.taxReports.title,
    url: '/taxReports',
    mainSubmenuItem: MenuItems.MainBooking,
    accessRights: [
      USER_RIGHTS.TAX_FORMS,
    ],
    subscriptionModule: [
      SUBSCRIPTION_MODULES.TAXFORM_DASHBOARD,
      SUBSCRIPTION_MODULES.TAXFORM_USTVA,
      SUBSCRIPTION_MODULES.TAXFORM_EUER,
      SUBSCRIPTION_MODULES.TAXFORM_TAX_FORM_SETTINGS,
      SUBSCRIPTION_MODULES.TAXFORM_TAX_FORM_HISTORY_LIST,
    ],
  },
  {
    name: MenuItems.Booking,
    title: menu.booking.booking,
    url: '/bookings',
    accessRights: [
      USER_RIGHTS.ACCOUNTING,
    ],
    subscriptionModule: SUBSCRIPTION_MODULES.BOOKINGS,
  },
  {
    name: MenuItems.Reporting,
    title: menu.booking.reporting,
    url: '/reporting',
    accessRights: [
      REPORTING_RIGHTS,
    ],
    subscriptionModule: SUBSCRIPTION_MODULES.REPORTING,
  },
  {
    name: MenuItems.EconomicAssets,
    title: menu.booking.economicAssets,
    mainSubmenuItem: MenuItems.MainBooking,
    url: '/economicAssets',
    accessRights: [
      USER_RIGHTS.ECONOMIC_ASSETS,
    ],
    subscriptionModule: SUBSCRIPTION_MODULES.ECONOMIC_ASSETS,
  },
]

const todoMenuItem = {
  name: MenuItems.Todos,
  title: menu.office.todos,
  url: '/todos',
  subscriptionModule: SUBSCRIPTION_MODULES.TODOS,
}

const firstStepsMenuItem = {
  name: MenuItems.FirstSteps,
  title: menu.firstSteps,
  url: '/firstSteps',
}

export const menuItemsCenter = (appsMenuItems) => {
  const {
    subscription: { details: { subscriptions = [], hasBackupAddon = false, backupDashboardMode = null } = {} } = {},
    user: { info: userInfo },
  } = store.getState()
  const mainSub = subscriptions.find((sub) => sub.typeOfSubscription === SubscriptionTypeTypes.Main)
  const isDemo = userInfo?.isDemo
  const onboardingNavigationEnabled = isOnboardingNavigationEnabled(subscriptions, isDemo)
  const hasSepaFeature = checkModuleAccess(SUBSCRIPTION_MODULES.DIRECT_DEBIT)
  const hasDirectDebitExperimentalFeature = user?.experimentalFeatures?.includes(experimentalFeatures.directDebit)
  const hasBackupModuleAccess = checkModuleAccess(SUBSCRIPTION_MODULES.BACKUP)
  const resolvedBackupDashboardMode = getResolvedBackupDashboardMode({
    backupDashboardMode,
    hasBackupModuleAccess,
    hasBackupAddon,
  })

  const items = [
    ...[onboardingNavigationEnabled ? firstStepsMenuItem : null].filter(Boolean),
    {
      name: MenuItems.Dashboard,
      title: menu.dashboard,
      url: '/dashboard',
    },
    {
      name: MenuItems.Office,
      title: menu.office.title,
      submenuItems: [
        todoMenuItem,
        {
          name: MenuItems.Letters,
          title: menu.office.letters,
          url: '/letters',
          accessRights: [
            USER_RIGHTS.LETTERS,
          ],
          subscriptionModule: SUBSCRIPTION_MODULES.LETTERS,
        },
      ],
    },
    {
      name: MenuItems.Clients,
      title: menu.masterData.clients,
      url: '/clients',
      accessRights: [
        USER_RIGHTS.CLIENTS,
      ],
      subscriptionModule: SUBSCRIPTION_MODULES.CLIENTS,
    },
    {
      name: MenuItems.MasterData,
      title: menu.masterData.title,
      submenuItems: [
        {
          name: MenuItems.Customers,
          title: menu.masterData.customers,
          url: '/customers',
          accessRights: [
            USER_RIGHTS.CUSTOMERS,
          ],
          subscriptionModule: SUBSCRIPTION_MODULES.CUSTOMERS,

        },
        {
          name: MenuItems.Suppliers,
          title: menu.masterData.suppliers,
          url: '/suppliers',
          accessRights: [
            USER_RIGHTS.SUPPLIERS,
          ],
          subscriptionModule: SUBSCRIPTION_MODULES.SUPPLIERS,
        },
        {
          name: MenuItems.Articles,
          title: menu.masterData.articles,
          url: '/articles',
          accessRights: [
            USER_RIGHTS.ARTICLES,
          ],
          subscriptionModule: SUBSCRIPTION_MODULES.ARTICLES,
        },
        {
          name: MenuItems.DataImport,
          title: menu.masterData.dataImport,
          url: '/import',
          accessRights: [
            USER_RIGHTS.CUSTOMERS,
            USER_RIGHTS.SUPPLIERS,
            USER_RIGHTS.ARTICLES,
          ],
          subscriptionModule: [SUBSCRIPTION_MODULES.CUSTOMERS, SUBSCRIPTION_MODULES.SUPPLIERS, SUBSCRIPTION_MODULES.ARTICLES],
        },
      ],
    },
    {
      name: MenuItems.Disposal,
      title: menu.disposal.title,
      submenuItems: [
        {
          name: MenuItems.Invoices,
          title: menu.disposal.invoices,
          url: '/invoices',
          accessRights: [
            USER_RIGHTS.INVOICES,
          ],
          subscriptionModule: SUBSCRIPTION_MODULES.INVOICES,
        },
        {
          name: MenuItems.Offers,
          title: menu.disposal.offers,
          url: '/offers',
          accessRights: [
            USER_RIGHTS.OFFERS,
          ],
          subscriptionModule: SUBSCRIPTION_MODULES.OFFERS,
        },
        {
          name: MenuItems.Orders,
          title: menu.disposal.orders,
          url: '/orders',
          accessRights: [
            USER_RIGHTS.ORDERS,
            USER_RIGHTS.DELIVERY_NOTES,
          ],
          subscriptionModule: SUBSCRIPTION_MODULES.ORDERSANDDELIVERYNOTES,
        },
        {
          name: MenuItems.DeliveryNotes,
          title: menu.disposal.deliveryNotes,
          url: '/deliverynotes',
          accessRights: [
            USER_RIGHTS.ORDERS,
            USER_RIGHTS.DELIVERY_NOTES,
          ],
          subscriptionModule: SUBSCRIPTION_MODULES.ORDERSANDDELIVERYNOTES,
        },
        {
          name: MenuItems.RecurringInvoices,
          title: menu.disposal.recurringInvoices,
          url: '/recurringinvoices',
          accessRights: [
            USER_RIGHTS.RECURRING_INVOICES,
          ],
          subscriptionModule: SUBSCRIPTION_MODULES.RECURRING_INVOICES,
        },
        {
          name: MenuItems.Projects,
          title: menu.disposal.projects,
          url: '/projects',
          accessRights: [
            USER_RIGHTS.PROJECTS,
          ],
          subscriptionModule: SUBSCRIPTION_MODULES.PROJECTS,
        },
        {
          name: MenuItems.Timetracking,
          title: menu.disposal.timetracking,
          url: '/timetrackings',
          accessRights: [
            USER_RIGHTS.TRACKED_TIMES,
          ],
          subscriptionModule: SUBSCRIPTION_MODULES.TIMETRACKING,
        },
      ],
    },
    {
      name: MenuItems.Obtaining,
      title: menu.obtaining.title,
      submenuItems: [
        {
          name: MenuItems.Purchases,
          title: menu.obtaining.purchases,
          url: '/purchases',
          accessRights: [
            USER_RIGHTS.PURCHASES,
          ],
          subscriptionModule: SUBSCRIPTION_MODULES.PURCHASES,
        },
      ],
    },
    {
      name: MenuItems.Finances,
      title: menu.finances.title,
      submenuItems: [
        {
          name: MenuItems.Banking,
          title: menu.finances.banking,
          url: '/banking',
          accessRights: [
            USER_RIGHTS.ACCOUNTING,
          ],
          subscriptionModule: SUBSCRIPTION_MODULES.BANKING,
        },
        ...(hasDirectDebitExperimentalFeature
          ? [{
            name: MenuItems.InvoiceDebitTransactions,
            title: lang.debitTransactions.title,
            url: '/banking/direct-debit-transactions',
            hide: !hasSepaFeature,
            accessRights: [
              USER_RIGHTS.ACCOUNTING,
            ],
            subscriptionModule: SUBSCRIPTION_MODULES.DIRECT_DEBIT,
          }]
          : []),
        {
          name: MenuItems.Expenses,
          title: menu.finances.expenses,
          url: '/expenses',
          accessRights: [
            USER_RIGHTS.EXPENSES,
          ],
          subscriptionModule: SUBSCRIPTION_MODULES.EXPENSES,
        },
        {
          name: MenuItems.Revenues,
          title: menu.finances.revenues,
          url: '/revenues',
          accessRights: [
            USER_RIGHTS.REVENUES,
          ],
          subscriptionModule: SUBSCRIPTION_MODULES.REVENUES,
        },
        ...(!isMB ? mainBookingSubItems : []),
      ],
    },
    {
      name: MenuItems.Documents,
      title: menu.documents,
      url: '/documents',
      purchasable: true,
      accessRights: [
        USER_RIGHTS.DOCUMENTS,
      ],
      subscriptionModule: SUBSCRIPTION_MODULES.DOCUMENTS,
      submenuItems: [],
      subscriptionTypeTypes: [
        SubscriptionTypeTypes.Document,
      ],
      showOnlyWithModuleAccess: isMeinBuero(), // only show when tenant has documents module (existing modules)
    },
    ...(resolvedBackupDashboardMode !== 'hidden'
      ? [{
        name: MenuItems.Backup,
        title: menu.backup,
        url: '/backup',
        forceKeep: resolvedBackupDashboardMode === 'readonly',
        subscriptionModule: SUBSCRIPTION_MODULES.BACKUP,
      }]
      : []),
    {
      name: MenuItems.Staff,
      title: menu.staff.title,
      url: '/staff',
      purchasable: true,
      purchasableOnlyWhenExpired: !isMB,
      accessRights: [
        USER_RIGHTS.STAFF,
      ],
      subscriptionModule: SUBSCRIPTION_MODULES.STAFF,
      submenuItems: [],
      subscriptionTypeTypes: [
        SubscriptionTypeTypes.Staff,
      ],
      showOnlyWithModuleAccess: isMeinBuero(), // only show when tenant has staff module (existing modules)
    },
    getAppsMenuItem(appsMenuItems),
  ]

  if (isMB) {
    items.push({
      name: MenuItems.MainBooking,
      title: menu.booking.title,
      submenuItems: [
        ...mainBookingSubItems,
      ],
    })
  }

  if ((!mainSub || mainSub.isExpired) && isMB && (!user.isDesktopTenant && !user.sessionFromDesktop)) {
    items.push(procurementMenuItem)
  }

  // REORDER FOR isMB:
  // Staff behind Procurement
  // Booking behind Finances
  if (isMB) {
    const financesIndex = items.findIndex(item => item.name === MenuItems.Finances)
    const mainBookingIndex = items.findIndex(item => item.name === MenuItems.MainBooking)
    if (financesIndex > -1 && mainBookingIndex > -1) {
      const [mainBookingItem] = items.splice(mainBookingIndex, 1)
      items.splice(financesIndex + 1, 0, mainBookingItem)
    }
    const procurementIndex = items.findIndex(item => item.name === MenuItems.Procurement)
    const staffIndex = items.findIndex(item => item.name === MenuItems.Staff)
    if (procurementIndex > -1 && staffIndex > -1) {
      const [staffItem] = items.splice(staffIndex, 1)
      items.splice(procurementIndex + 1, 0, staffItem)
    }
  }

  if (!isMB && user?.experimentalFeatures?.includes(experimentalFeatures.marketPlace)) {
    items.push({
      name: MenuItems.Marketplace,
      title: menu.marketPlace.title,
      url: '/shop',
      accessRights: [
        USER_RIGHTS.SUBSCRIPTIONS,
      ],
    })
  }

  // FLAG:docuAiEnhancements — "Meine Meldungen" (reported analysis problems + state).
  // Internal-only for now (PO holdback): rides the child flag, not useDocuAi itself.
  if (user?.experimentalFeatures?.includes(experimentalFeatures.docuAiEnhancements)) {
    items.push({
      name: MenuItems.CustomerIssues,
      title: menu.customerIssues,
      url: '/customer-issues',
    })
  }

  return handleAccessRights(items)
}

export const getSettingsMenuItem = (userInfo, tenantInfo, checkAccess = false) => {
  const showUnavailableFeatures = userInfo?.layoutSettings?.showUnavailableFeatures ?? false
  const isTaxConsultant = tenantInfo?.subscriptions?.some(s => s.isTaxConsultant)
  const canSeeDisabledItems = !isMB && !isTaxConsultant && showUnavailableFeatures && checkIsAdminRole(userInfo)

  const isTaxAccountant = tenantInfo?.subscriptions?.some(({ isTaxConsultant }) => isTaxConsultant)
  let submenuItems = [
    {
      name: MenuItems.MyAccount,
      title: lang.settings.pageTitles.myAccount,
      url: '/settings/myaccount',
      mainSubmenuItem: MenuItems.Settings,
      accessRights: [
        SETTINGS_RIGHTS.ACCOUNT,
      ],
    },
  ]

  if (tenantInfo?.subscriptions?.length) {
    const identifier = tenantInfo?.subscriptions?.some(s => s.isTaxConsultant) ? 'Office' : 'Company'

    submenuItems = [
      ...submenuItems,
      {
        name: `my${identifier.toLocaleLowerCase()}`,
        title: lang.settings.pageTitles[`my${identifier}`],
        url: `/settings/my${identifier.toLocaleLowerCase()}`,
        mainSubmenuItem: MenuItems.Settings,
        accessRights: [
          SETTINGS_RIGHTS.COMPANY,
        ],
      },
    ]
  }

  submenuItems = [
    ...submenuItems,
    {
      name: MenuItems.LetterPaper,
      title: lang.settings.pageTitles.letterPaper,
      url: '/settings/letter-paper',
      mainSubmenuItem: MenuItems.Settings,
      accessRights: [
        SETTINGS_RIGHTS.LETTER_PAPER,
      ],
      subscriptionModule: [
        SUBSCRIPTION_MODULES.INVOICES,
        SUBSCRIPTION_MODULES.OFFERS,
        SUBSCRIPTION_MODULES.ORDERSANDDELIVERYNOTES,
        SUBSCRIPTION_MODULES.DUNNING,
        SUBSCRIPTION_MODULES.RECURRING_INVOICES,
      ],
    },
    {
      name: MenuItems.CustomerCenter,
      title: lang.settings.pageTitles.customerCenter,
      url: '/settings/customer-center',
      mainSubmenuItem: MenuItems.Settings,
      subscriptionTypeTypes: [
        SubscriptionTypeTypes.Main,
        SubscriptionTypeTypes.Document,
        SubscriptionTypeTypes.TaxConsultant,
        SubscriptionTypeTypes.Desktop,
      ],
    },
    {
      name: MenuItems.Interfaces,
      title: lang.settings.pageTitles.interfaces,
      url: '/settings/interfaces',
      mainSubmenuItem: MenuItems.Settings,
      purchasable: false,
      subscriptionTypeTypes: [
        SubscriptionTypeTypes.Main,
      ],
      accessRights: [
        SETTINGS_RIGHTS.ECOMMERCE,
      ],
      subscriptionModule: [
        SUBSCRIPTION_MODULES.SHOP_CONNECTION,
        SUBSCRIPTION_MODULES.SHIPPING_CONNECTION,
      ],
    },
    {
      name: MenuItems.MasterData,
      title: lang.settings.pageTitles.masterdata,
      url: '/settings/masterdata',
      mainSubmenuItem: MenuItems.MasterData,
      accessRights: [
        SETTINGS_RIGHTS.MASTERDATA,
      ],
      subscriptionModule: [
        SUBSCRIPTION_MODULES.INVOICES,
        SUBSCRIPTION_MODULES.OFFERS,
        SUBSCRIPTION_MODULES.ORDERSANDDELIVERYNOTES,
        SUBSCRIPTION_MODULES.DUNNING,
        SUBSCRIPTION_MODULES.RECURRING_INVOICES,
      ],
    },
    {
      name: MenuItems.CustomFields,
      title: lang.settings.pageTitles.customFields,
      url: '/settings/customFields',
      mainSubmenuItem: 'settings',
      accessRights: [USER_RIGHTS.ARTICLES, USER_RIGHTS.CUSTOMERS],
      subscriptionModule: [SUBSCRIPTION_MODULES.ARTICLES, SUBSCRIPTION_MODULES.CUSTOMERS],
    },
    {
      name: MenuItems.MoreSettings,
      title: lang.moreSettingsHeading,
      url: '/settings/more-settings',
      mainSubmenuItem: MenuItems.Settings,
      writeAccess: true,
      matchAnyAccessRight: true,
      accessRights: [
        USER_RIGHTS.CUSTOMERS,
        USER_RIGHTS.SUPPLIERS,
        USER_RIGHTS.ARTICLES,
        USER_RIGHTS.TRACKED_TIMES,
        USER_RIGHTS.INVOICES,
        USER_RIGHTS.OFFERS,
        USER_RIGHTS.ORDERS,
        USER_RIGHTS.DELIVERY_NOTES,
      ],
      subscriptionModule: [
        SUBSCRIPTION_MODULES.CUSTOMERS,
        SUBSCRIPTION_MODULES.SUPPLIERS,
        SUBSCRIPTION_MODULES.ARTICLES,
        SUBSCRIPTION_MODULES.TIMETRACKING,
        SUBSCRIPTION_MODULES.INVOICES,
        SUBSCRIPTION_MODULES.OFFERS,
        SUBSCRIPTION_MODULES.ORDERSANDDELIVERYNOTES,
      ],
    },
    {
      name: MenuItems.Users,
      title: isTaxAccountant ? lang.settingSectionTitles.user : lang.settingSectionTitles.userAndTaxConsultant,
      url: '/users',
      mainSubmenuItem: MenuItems.Settings,
      writeAccess: true,
      accessRights: [
        USER_RIGHTS.MEMBERS,
      ],
    },
    {
      name: MenuItems.EventLog,
      title: lang.settingSectionTitles.eventLog,
      url: '/settings/event-log',
      mainSubmenuItem: MenuItems.Settings,
      writeAccess: true,
      accessRights: [
        SETTINGS_RIGHTS.EVENT_LOG,
      ],
    },
  ]

  if (isElectron()) {
    submenuItems.push({
      name: MenuItems.Electron,
      title: menu.settings.electron,
      url: '/settings/electron',
      mainSubmenuItem: MenuItems.Settings,
      writeAccess: true,
    })
  }

  const autoMailboxAccess = checkModuleAccess(SUBSCRIPTION_MODULES.AUTO_MAILBOX)
  if (autoMailboxAccess || canSeeDisabledItems) {
    submenuItems.push({
      name: MenuItems.Mailbox,
      title: lang.settings.pageTitles.mailbox,
      url: '/settings/mailbox',
      mainSubmenuItem: MenuItems.Settings,
      subscriptionModule: [
        SUBSCRIPTION_MODULES.AUTO_MAILBOX,
      ],
    })
  }

  const settingsItem = {
    name: MenuItems.Settings,
    title: menu.settings.title,
    submenuItems,
  }

  if (!checkAccess) { return settingsItem }
  return handleAccessRights([settingsItem])[0]
}

export const menuItemsFooter = (userInfo, tenantInfo) => {
  const settingsItem = getSettingsMenuItem(userInfo, tenantInfo)
  const items = [settingsItem]
  return handleAccessRights(items)
}

export const procurementMenuItem = {
  name: MenuItems.Procurement,
  title: menu.procurement,
  url: null, // menu point is just for starting trial and upgrading
  purchasable: true,
  submenuItems: [],
  subscriptionTypeTypes: [
    SubscriptionTypeTypes.Main,
  ],
}

export const getMarketplaceMenuItem = (userInfo, checkAccess) => {
  if (!user?.experimentalFeatures?.includes(experimentalFeatures.marketPlace)) return
  const marketplaceItem = {
    name: MenuItems.Marketplace,
    title: menu.marketPlace.title,
    url: '/shop',
    accessRights: [
      USER_RIGHTS.SUBSCRIPTIONS,
    ],
  }
  if (!checkAccess) { return marketplaceItem }
  return handleAccessRights([marketplaceItem])[0]
}
